// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Alert, Button, TextArea, TextInput, ToggleSwitch, useToast } from '@loa/ui-components';
import { proposalApi } from '../../../../api/proposalApi';
import type {
  PensionRecommendationSectionContent,
  ProposalSection,
  RecommendationOptionSummary,
  RecommendedFundLine,
} from '../../../../types/proposal';
import { formatCurrency } from '../../proposalDisplay';
import { useAiBlockDraft } from '../useAiBlockDraft';
import { AiGenerateRow } from './AiGenerateRow';
import { FundAllocationEditor } from '../../../funds/FundAllocationEditor';

/**
 * Fund selection only makes sense for products that hold funds — PENSION and
 * INVESTMENT. PROTECTION is insurance cover with no fund allocation, so the
 * shared recommendation section hides the fund editor for it.
 */
function sectionHasFunds(sectionKey: string): boolean {
  return sectionKey === 'PENSION_RECOMMENDATION' || sectionKey === 'INVESTMENT_RECOMMENDATION';
}

/**
 * Seed the fund editor from the section content: prefer the structured
 * `recommendedFunds` array; fall back to a single manual line derived from the
 * legacy free-text `recommendedFund` string.
 */
function seedFunds(content: PensionRecommendationSectionContent): RecommendedFundLine[] {
  if (content.recommendedFunds && content.recommendedFunds.length > 0) {
    return content.recommendedFunds;
  }
  if (content.recommendedFund && content.recommendedFund.trim().length > 0) {
    return [{ fundName: content.recommendedFund.trim() }];
  }
  return [];
}

export type SectionQuoteRecommendationProps = Readonly<{
  proposalId: string;
  /** Anchors the AI session + the back-to-wizard deep-link. */
  profileId?: string;
  section: ProposalSection;
  onSaved: (updated: ProposalSection) => void;
  /**
   * When provided (the sectioned builder hosting Quote Options as a rail
   * section), the "no option selected" guidance switches the rail to
   * QUOTE_OPTIONS instead of deep-linking back to the v1 wizard route.
   */
  onOpenQuoteOptions?: () => void;
  /**
   * Renders the "Client view" preview (mirrors the Breakthrough Report's
   * Advisor view <-> Client view toggle): the recommended-option card,
   * figures and comparison table are already read-only and render
   * unchanged; the "Open quote options" link, the AI-generate row and the
   * amount/fund/rationale/comparison-toggle inputs are hidden or disabled,
   * and the Save button is hidden. Defaults to false (advisor editable
   * builder).
   */
  readOnly?: boolean;
}>;

function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { message?: string } | undefined)?.message ?? fallback;
  }
  return fallback;
}

function readContent(section: ProposalSection): PensionRecommendationSectionContent {
  return (section.content as PensionRecommendationSectionContent | null) ?? {};
}

const formatPercent = (value: number | undefined, decimals = 1): string => {
  if (value == null) return '—';
  if (Number.isInteger(value)) return `${value}%`;
  return `${value.toFixed(decimals)}%`;
};

/** Charge schedule joined " / " (e.g. year-by-year FMC); "NIL" when empty. */
const formatSchedule = (values: readonly number[] | undefined): string => {
  if (!values || values.length === 0) return 'NIL';
  return values.map((v) => formatPercent(v)).join(' / ');
};

/** Rows of the option cards + comparison table, in display order. */
const CHARGE_ROWS: ReadonlyArray<{
  label: string;
  value: (option: RecommendationOptionSummary) => string;
}> = [
  {
    label: 'Net Allocation',
    value: (o) => formatPercent(o.figures?.allocationPercent),
  },
  { label: 'AMC', value: (o) => (o.figures?.amcPercent != null ? `${formatPercent(o.figures.amcPercent, 2)} p.a.` : '—') },
  { label: 'Fund Management Charge', value: (o) => formatSchedule(o.figures?.fundManagementCharges) },
  { label: 'Exit Penalties', value: (o) => formatSchedule(o.figures?.exitPenalties) },
  { label: 'Clawback', value: (o) => formatSchedule(o.figures?.clawbacks) },
];

/**
 * Quote-driven recommendation section (`*_RECOMMENDATION` — PENSION,
 * INVESTMENT, PROTECTION per docs/specs/investment-protection-proposals-prd.md
 * RQ-4) — the server-owned "Recommended option" facts card (derived from the
 * wizard's selected quote option), the advisor-editable recommendation fields
 * (amount / fund / rationale — the rationale offers AI drafting), the
 * provider comparison toggle + read-only table of pinned alternatives, and
 * the staleness banner the server raises when the quote selection changed
 * under an existing rationale (saving acknowledges + clears it server-side).
 *
 * Fully generic over the recommendation type: every product-specific string
 * (the AI drafting message, the "X saved" toast) is derived from
 * `section.sectionKey` / `section.title`, both server-supplied — no per-type
 * prop is needed. `PENSION_RECOMMENDATION` / `INVESTMENT_RECOMMENDATION` /
 * `PROTECTION_RECOMMENDATION` all render through this one component.
 */
export function SectionQuoteRecommendation({
  proposalId,
  profileId,
  section,
  onSaved,
  onOpenQuoteOptions,
  readOnly = false,
}: SectionQuoteRecommendationProps) {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const initial = readContent(section);

  const showFunds = sectionHasFunds(section.sectionKey);
  const [recommendedAmount, setRecommendedAmount] = useState(
    initial.recommendedAmount != null ? String(initial.recommendedAmount) : '',
  );
  const [recommendedFunds, setRecommendedFunds] = useState<RecommendedFundLine[]>(() =>
    seedFunds(initial),
  );
  const [rationale, setRationale] = useState(initial.rationale ?? '');
  const [comparisonVisible, setComparisonVisible] = useState(initial.comparisonVisible ?? true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Server-owned pieces render from the latest section prop, not local state.
  const content = readContent(section);
  const selected = content.selected;
  const comparison = content.comparison ?? [];

  const handleAiApplied = useCallback(
    (updated: ProposalSection) => {
      // Only the rationale block was persisted — sync it from the response
      // and keep any unsaved amount/fund edits the advisor has in flight.
      const next = readContent(updated);
      setRationale(next.rationale ?? '');
      onSaved(updated);
      showToast('AI rationale applied', 'success');
    },
    [onSaved, showToast],
  );

  const ai = useAiBlockDraft({
    proposalId,
    profileId,
    sectionKey: section.sectionKey,
    block: 'rationale',
    buildMessage: (instruction) =>
      `Draft the rationale block of the ${section.title.toLowerCase()} section for proposal ${proposalId}.${
        instruction ? ` Instruction: ${instruction}` : ''
      }`,
    buildPayload: (parsed) => {
      if (typeof parsed.rationale !== 'string' || parsed.rationale.trim().length === 0) {
        throw new Error('missing rationale');
      }
      return { rationale: parsed.rationale };
    },
    onApplied: handleAiApplied,
  });

  const amountNumber = useMemo(() => {
    const trimmed = recommendedAmount.trim();
    if (trimmed.length === 0) return undefined;
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? undefined : parsed;
  }, [recommendedAmount]);

  const handleSave = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Send the whole content object — the server discards any client-sent
      // `selected`/`comparison` and re-derives them from the quote; the save
      // also clears `staleSinceQuoteEdit`.
      // Persist the structured funds array plus a comma-joined summary string
      // in the legacy `recommendedFund` field so older readers keep working.
      // PROTECTION has no funds — leave whatever the content already carries.
      const fundSummary = recommendedFunds.map((f) => f.fundName).join(', ');
      const updated = await proposalApi.updateSection(proposalId, section.sectionKey, {
        content: {
          ...content,
          recommendedAmount: amountNumber,
          recommendedFund: showFunds ? fundSummary || undefined : content.recommendedFund,
          recommendedFunds: showFunds ? recommendedFunds : content.recommendedFunds,
          rationale,
          comparisonVisible,
        },
      });
      setDirty(false);
      onSaved(updated);
      showToast(`${section.title} saved`, 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, `Failed to save ${section.title}`), 'error');
    } finally {
      setSaving(false);
    }
  }, [saving, proposalId, section.sectionKey, section.title, content, amountNumber, showFunds, recommendedFunds, rationale, comparisonVisible, onSaved, showToast]);

  const handleBackToQuoteOptions = useCallback(() => {
    if (onOpenQuoteOptions) {
      // The builder hosts Quote Options as its own rail section — switch the
      // rail rather than leaving the page for the v1 wizard route.
      onOpenQuoteOptions();
      return;
    }
    if (profileId) {
      navigate(`/relationships/${profileId}/proposals/pension-wizard/${proposalId}`);
    }
  }, [onOpenQuoteOptions, navigate, profileId, proposalId]);

  return (
    <div className="pension-builder__section" data-testid="section-pension-recommendation">
      {content.staleSinceQuoteEdit === true && (
        <div data-testid="section-recommendation-stale-banner">
          <Alert variant="warning">
            The selected quote option changed since this section was written — review the
            rationale and save to acknowledge.
          </Alert>
        </div>
      )}

      {selected ? (
        <div className="pension-builder__facts-card" data-testid="section-recommendation-selected">
          <div className="pension-builder__option-header">
            {selected.providerLogoUrl && (
              <img
                src={selected.providerLogoUrl}
                alt={selected.providerName ?? 'Provider'}
                className="pension-builder__option-logo"
              />
            )}
            <div>
              <h3 className="pension-builder__facts-card-title">
                {selected.providerName ?? 'Recommended option'}
              </h3>
              <p className="pension-builder__option-subtitle">
                Recommended option
                {selected.figures?.amount != null &&
                  ` · ${formatCurrency(selected.figures.amount, selected.figures.currencyCode)}`}
              </p>
            </div>
          </div>
          <dl className="pension-builder__facts-grid">
            {CHARGE_ROWS.map((row) => (
              <div className="pension-builder__fact" key={row.label}>
                <dt className="pension-builder__fact-label">{row.label}</dt>
                <dd className="pension-builder__fact-value">{row.value(selected)}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : (
        <div className="pension-builder__placeholder" data-testid="section-recommendation-no-option">
          <div>
            <p className="pension-builder__placeholder-text">
              No quote option is selected yet — pick one on the wizard's Quote Options step first.
            </p>
            {!readOnly && (onOpenQuoteOptions || profileId) && (
              <Button
                variant="outlined"
                size="sm"
                onClick={handleBackToQuoteOptions}
                data-testid="section-recommendation-open-quote-options"
              >
                Open quote options
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="pension-builder__field-row">
        <TextInput
          label="Recommended Amount"
          type="number"
          value={recommendedAmount}
          onChange={(e) => {
            setRecommendedAmount(e.target.value);
            setDirty(true);
          }}
          placeholder="e.g. 250000"
          fluid
          disabled={readOnly}
          data-testid="section-recommendation-amount"
        />
      </div>

      {showFunds && (
        <div className="pension-builder__funds" data-testid="section-recommendation-funds">
          <p className="pension-builder__facts-card-title">Recommended Funds</p>
          <FundAllocationEditor
            value={recommendedFunds}
            onChange={(lines) => {
              setRecommendedFunds(lines);
              setDirty(true);
            }}
            disabled={readOnly}
            testId="section-recommendation-fund-editor"
          />
        </div>
      )}

      {!readOnly && (
        <AiGenerateRow
          idSuffix="recommendation-rationale"
          instruction={ai.instruction}
          onInstructionChange={ai.setInstruction}
          onGenerate={() => void ai.generate()}
          generating={ai.generating}
          error={ai.error}
        />
      )}
      <TextArea
        label="Rationale"
        value={rationale}
        onChange={(e) => {
          setRationale(e.target.value);
          setDirty(true);
        }}
        placeholder="Why this option is right for the client…"
        rows={6}
        fluid
        disabled={readOnly || ai.generating}
        data-testid="section-recommendation-rationale"
      />

      <div className="pension-builder__toggle-row" data-testid="section-recommendation-comparison-toggle">
        <ToggleSwitch
          checked={comparisonVisible}
          onChange={(checked) => {
            setComparisonVisible(checked);
            setDirty(true);
          }}
          disabled={readOnly}
          aria-label="Include provider comparison in the document"
        />
        <span className="pension-builder__toggle-label">
          Include provider comparison in the document
        </span>
      </div>

      {comparisonVisible && comparison.length > 0 && selected && (
        <table className="pension-builder__table" data-testid="section-recommendation-comparison">
          <thead>
            <tr>
              <th>Charge</th>
              <th>{`${selected.providerName ?? 'Selected'} (recommended)`}</th>
              {comparison.map((option) => (
                <th key={option.optionId ?? option.providerId ?? option.providerName}>
                  {option.providerName ?? '—'}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CHARGE_ROWS.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td>{row.value(selected)}</td>
                {comparison.map((option) => (
                  <td key={option.optionId ?? option.providerId ?? option.providerName}>
                    {row.value(option)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!readOnly && (
        <div className="pension-builder__section-footer">
          <Button
            variant="filled"
            onClick={handleSave}
            disabled={saving || !dirty}
            loading={saving}
            data-testid="section-recommendation-save-btn"
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
}

export default SectionQuoteRecommendation;
